import { Route } from '@angular/router';
import { RegistrationSuccessPageComponent } from './pages/registration-success-page/registration-success-page.component';
import { RegistrationFailurePageComponent } from './pages/registration-failure-page/registration-failure-page.component';
import { RegistrationEntryPageComponent } from './pages/registration-entry-page/registration-entry-page.component';
import {
  AddressResolver,
  AuthGuard,
  CountryResolver,
  MaintenanceGuard,
  MemberResolver,
  RegistrationMembershipOptionResolver,
  RegistrationStepResolver,
  StepType,
  WizardType,
  registrationResolver,
} from '@fgb/core';
import { RegistrationWizardDisabledPageComponent } from './pages/registration-wizard-disabled-page/registration-wizard-disabled-page.component';
import { RegistrationPersonalDetailsComponent } from './registration-personal-details/registration-personal-details.component';
import { RegistrationGetProviderDetailsComponent } from './registration-get-provider-details/registration-get-provider-details.component';
import { RegistrationAddChildrenComponent } from './registration-add-children/registration-add-children.component';
import { RegistrationMembershipOptionsComponent } from './registration-membership-options/registration-membership-options.component';
import { RegistrationChooseMembershipOptionTypeComponent } from './registration-choose-membership-option-type/registration-choose-membership-option-type.component';
import { RegistrationShippingAddressComponent } from './registration-shipping-address/registration-shipping-address.component';
import { RegistrationTermsAndConditionsComponent } from './registration-terms-and-conditions/registration-terms-and-conditions.component';
import { RegistrationProviderAuthenticationComponent } from './registration-provider-authentication/registration-provider-authentication.component';
import { RegistrationProviderTranslationComponent } from './registration-provider-translation/registration-provider-translation.component';
import { RegistrationOperatorNameComponent } from './registration-operator-name/registration-operator-name.component';
import { RegistrationChildQuestionsComponent } from './registration-child-questions/registration-child-questions.component';
import { RegistrationQuestionsComponent } from './registration-questions/registration-questions.component';
import { RegistrationPartnerInviteComponent } from './registration-partner-invite/registration-partner-invite.component';
import { RegistrationOptOutAlertsComponent } from './registration-opt-out-alerts/registration-opt-out-alerts.component';
import { RegistrationOptOutEmailsComponent } from './registration-opt-out-emails/registration-opt-out-emails.component';
import { RegistrationProviderOauthComponent } from './registration-provider-oauth/registration-provider-oauth.component';
import { RegistrationPaymentCallbackPageComponent } from './pages/registration-payment-callback-page/registration-payment-callback-page.component';
import { RegistrationMarketingPlaceholderComponent } from './registration-marketing-placeholder/registration-marketing-placeholder.component';
import { RegistrationPaymentComponent } from './registration-payment/registration-payment.component';
import { RegistrationReferAFriendPageComponent } from './registration-refer-a-friend/registration-refer-a-friend.component';
import { RegistrationSelectCustomerMembershipOptionComponent } from './registration-select-membership-option/registration-select-membership-option.component';
import { RegistrationBenefitOptionsComponent } from './registration-benefits/registration-benefits-options.component';
import { RegistrationAccountVerificationComponent } from './registration-account-verification/registration-account-verification.component';
import { friendKeyMatchGuard } from './registration-refer-a-friend-with-form/registration-refer-a-friend-with-form.component';

export const registrationRoutes: Route[] = [
  {
    path: '',
    resolve: { modulePath: registrationResolver, country: CountryResolver },
    canActivateChild: [MaintenanceGuard],
    children: [
      {
        path: 'success',
        component: RegistrationSuccessPageComponent,
      },
      {
        path: 'failure',
        component: RegistrationFailurePageComponent,
      },
      // Entry points
      {
        path: 'firsttime',
        component: RegistrationEntryPageComponent,
        canActivate: [AuthGuard],
        data: { wizardType: WizardType.FirstTimeRegistration },
      },
      {
        path: 'quick',
        component: RegistrationEntryPageComponent,
        data: { wizardType: WizardType.QuickRegistration },
      },
      {
        path: 'partner',
        component: RegistrationEntryPageComponent,
        data: { wizardType: WizardType.PartnerRegistration },
      },
      {
        path: 'new-member',
        component: RegistrationEntryPageComponent,
        data: { wizardType: WizardType.NewMemberRegistration },
      },
      {
        path: 'guardian',
        component: RegistrationEntryPageComponent,
        data: { wizardType: WizardType.GuardianRegistration },
      },
      {
        path: 'newchildren',
        component: RegistrationEntryPageComponent,
        data: { wizardType: WizardType.ChildRegistration },
      },
      {
        path: 'external',
        component: RegistrationEntryPageComponent,
        data: { wizardType: WizardType.External },
      },
      {
        path: 'in-park',
        component: RegistrationEntryPageComponent,
        data: { wizardType: WizardType.InPark },
      },
      {
        path: 'social',
        component: RegistrationEntryPageComponent,
        data: { wizardType: WizardType.SocialRegistration },
      },
      {
        path: 'upgrade-member',
        component: RegistrationEntryPageComponent,
        data: { wizardType: WizardType.UpgradeMembership },
      },
      {
        path: 'wizardDisabled',
        component: RegistrationWizardDisabledPageComponent,
      },
      {
        path: 'referafriend',
        component: RegistrationEntryPageComponent,
        data: { wizardType: WizardType.ReferAFriend },
      },
      {
        path: 'verification',
        component: RegistrationEntryPageComponent,
        data: { wizardType: WizardType.AccountVerification },
      },
      // Steps
      // Refer a Friend step with form to enter friend key manually, if friend key is absent in query params use this component else load normal refer a friend step
      {
        path: 'friend',
        loadComponent: () =>
          import('./registration-refer-a-friend-with-form/registration-refer-a-friend-with-form.component').then(
            (m) => m.RegistrationReferAFriendWithFormComponent
          ),
        canMatch: [friendKeyMatchGuard],
      },
      // Legacy refer a friend step with friend key in query params, if not provided user will be redirected to the above component to enter it manually
      {
        path: 'friend',
        component: RegistrationReferAFriendPageComponent,
        data: { registrationStep: StepType.ReferAFriend },
      },
      {
        path: 'details',
        component: RegistrationPersonalDetailsComponent,
        resolve: { registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.PersonalDetails },
      },
      {
        path: 'getproviderdetails',
        component: RegistrationGetProviderDetailsComponent,
        resolve: { registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.GetProviderDetails },
      },
      {
        path: 'children',
        component: RegistrationAddChildrenComponent,
        resolve: { registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.AddChild, minAge: 0, maxAge: 12, childLimit: 0 },
      },
      {
        path: 'memberships',
        component: RegistrationMembershipOptionsComponent,
        resolve: { membershipOptions: RegistrationMembershipOptionResolver, registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.MembershipOption },
      },
      {
        path: 'benefits',
        component: RegistrationBenefitOptionsComponent,
        resolve: { registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.BenefitOptions },
      },
      {
        path: 'select-membership',
        component: RegistrationSelectCustomerMembershipOptionComponent,
        data: { registrationStep: StepType.SelectCustomerMembershipOption },
      },
      {
        path: 'choosersn',
        component: RegistrationChooseMembershipOptionTypeComponent,
        resolve: { membershipOptions: RegistrationMembershipOptionResolver, registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.ChooseMembershipOption },
      },
      {
        path: 'shipping',
        component: RegistrationShippingAddressComponent,
        resolve: { registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.ShippingAddress },
      },
      {
        path: 'terms',
        component: RegistrationTermsAndConditionsComponent,
        resolve: { registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.TermsAndConditions },
      },
      {
        path: 'authentication',
        component: RegistrationProviderAuthenticationComponent,
        resolve: { registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.ProviderAuthentication2 },
      },
      {
        path: 'accounts',
        component: RegistrationProviderTranslationComponent,
        resolve: { registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.ProviderTranslation },
      },
      {
        path: 'operatorname',
        component: RegistrationOperatorNameComponent,
        resolve: { registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.OperatorName },
      },
      {
        path: 'childquestions',
        component: RegistrationChildQuestionsComponent,
        resolve: { registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.ChildQuestions },
      },
      {
        path: 'questions',
        component: RegistrationQuestionsComponent,
        resolve: { registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.Questions },
      },
      {
        path: 'partnerinvite',
        component: RegistrationPartnerInviteComponent,
        resolve: { registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.ProcessPartnerRequest },
      },
      {
        path: 'optoutalerts',
        component: RegistrationOptOutAlertsComponent,
        resolve: { registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.OptOutAlerts },
      },
      {
        path: 'optoutemails',
        component: RegistrationOptOutEmailsComponent,
        resolve: { registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.OptOutEmails },
      },
      {
        path: 'provideroauth',
        component: RegistrationProviderOauthComponent,
        resolve: { registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.ProviderOAuth },
      },
      {
        path: 'verify',
        component: RegistrationAccountVerificationComponent,
        resolve: { registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.AccountVerify },
      },
      {
        path: 'payment',
        children: [
          {
            path: '',
            component: RegistrationPaymentComponent,
            data: { registrationStep: StepType.Payment },
            resolve: { member: MemberResolver, address: AddressResolver },
          },
          {
            resolve: { member: MemberResolver, address: AddressResolver },
            path: 'result',
            component: RegistrationPaymentCallbackPageComponent,
          },
        ],
        resolve: { registrationStep: RegistrationStepResolver },
      },
      {
        path: 'welcome',
        component: RegistrationMarketingPlaceholderComponent,
        data: { registrationStep: StepType.Marketing },
      },
    ],
  },
];

export function mergeRegistrationRoutes(routes: Route[] = []): Route[] {
  let foundRoutes = registrationRoutes[0].children?.filter((r) => routes.some((rr) => rr.path === r.path)) || [];
  const newRoutes = routes.filter((r) => !foundRoutes.some((rr) => rr.path === r.path));
  const existingRoutes = registrationRoutes[0].children?.filter((r) => !foundRoutes.some((rr) => rr.path === r.path)) || [];

  if (foundRoutes.length > 0) {
    foundRoutes = foundRoutes.map((r) => {
      const route = routes.find((rr) => rr.path === r.path);
      return { ...r, ...route, data: route?.data ? { ...r.data, ...route.data } : r.data };
    });
  }
  return [
    {
      ...registrationRoutes[0],
      children: [...existingRoutes, ...foundRoutes, ...newRoutes],
    },
  ];
}
